import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    words: { type: Array, default: [] },
    stampGap: { type: Number, default: 500 },
    holdDuration: { type: Number, default: 1600 }
  }

  static targets = ["stamp"]

  static tilts = [-18, -14, -10, -6, 6, 10, 14, 18]
  static offsetRange = 10

  connect() {
    if (this.wordsValue.length === 0 || this.stampTargets.length === 0) return

    this.wordIndex = 0
    this.isFirstCycle = true
    this.slotTilts = this.stampTargets.map((_, index) => this.constructor.tilts[index % this.constructor.tilts.length])
    this.slotOffsets = this.stampTargets.map(() => this.randomOffset())
    this.setSlotWords()
    this.applyPlacements()
    this.prepareStamps()

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.showAllStatic()
      return
    }

    this.cycle()
  }

  disconnect() {
    clearTimeout(this.cycleTimer)
  }

  async cycle() {
    if (this.isFirstCycle) {
      await this.stampSequence()
      this.isFirstCycle = false
    } else {
      this.wordIndex = (this.wordIndex + this.stampTargets.length) % this.wordsValue.length
      await this.stampSequence()
    }

    await this.wait(this.holdDurationValue)
    this.cycleTimer = setTimeout(() => this.cycle(), 0)
  }

  async stampSequence() {
    for (let i = 0; i < this.stampTargets.length; i++) {
      await this.stampOne(i)
      if (i < this.stampTargets.length - 1) await this.wait(this.stampGapValue)
    }
  }

  stampOne(index) {
    const stamp = this.stampTargets[index]
    stamp.textContent = this.wordForSlot(index)
    this.slotTilts[index] = this.nextTiltForSlot(index)
    this.slotOffsets[index] = this.nextOffsetForSlot(index)
    this.applyPlacement(stamp, index)
    stamp.classList.remove("is-stamped", "is-stamping", "is-updating")
    void stamp.offsetWidth
    stamp.classList.add("is-stamping")

    return new Promise((resolve) => {
      stamp.addEventListener(
        "animationend",
        () => {
          stamp.classList.remove("is-stamping")
          stamp.classList.add("is-stamped")
          resolve()
        },
        { once: true }
      )
    })
  }

  wordForSlot(slotIndex) {
    return this.wordsValue[(this.wordIndex + slotIndex) % this.wordsValue.length]
  }

  setSlotWords() {
    this.stampTargets.forEach((stamp, index) => {
      stamp.textContent = this.wordForSlot(index)
    })
  }

  randomOffset() {
    const range = this.constructor.offsetRange
    return {
      x: Math.round((Math.random() * range * 2 - range) * 10) / 10,
      y: Math.round((Math.random() * range * 2 - range) * 10) / 10
    }
  }

  nextTiltForSlot(index) {
    const current = this.slotTilts[index]
    const options = this.constructor.tilts.filter((tilt) => tilt !== current)
    return options[Math.floor(Math.random() * options.length)]
  }

  nextOffsetForSlot(index) {
    const current = this.slotOffsets[index]
    let next = this.randomOffset()
    let attempts = 0

    while (attempts < 8 && this.offsetDistance(current, next) < 4) {
      next = this.randomOffset()
      attempts++
    }

    return next
  }

  offsetDistance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  applyPlacement(stamp, index) {
    const { x, y } = this.slotOffsets[index]
    stamp.style.setProperty("--stamp-x", `${x}%`)
    stamp.style.setProperty("--stamp-y", `${y}%`)
    stamp.style.setProperty("--stamp-tilt", `${this.slotTilts[index]}deg`)
  }

  applyPlacements() {
    this.stampTargets.forEach((stamp, index) => this.applyPlacement(stamp, index))
  }

  prepareStamps() {
    this.stampTargets.forEach((stamp) => {
      stamp.classList.remove("is-stamped", "is-stamping", "is-updating")
    })
  }

  showAllStatic() {
    this.setSlotWords()
    this.applyPlacements()
    this.stampTargets.forEach((stamp) => stamp.classList.add("is-stamped"))
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
